import FilterToolbar from '../components/FilterToolbar'
import { useEffect, useMemo, useState } from 'react'
import { Edit3, Plus, Target, Wallet } from 'lucide-react'
import Modal from '../components/Modal'
import MoneyInput from '../components/MoneyInput'
import { usePeriod } from '../context/PeriodContext'
import { useRegisterQuickCreate } from '../context/QuickCreateContext'
import { dataClient } from '../services/dataClient'
import { listarCategorias, listarTabelaGerencial, obterAcessoBase, obterBaseAtiva, temPermissao } from '../services/financeiro'
import type { BaseFinanceira, Categoria } from '../types/database'

type Mode = 'orcamentos' | 'metas'
type Row = Record<string, any>
const currency = (n:number) => new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(n)
const yearMonth = (p:string) => /^\d{4}-\d{2}$/.test(p) ? p : new Date().toISOString().slice(0,7)
const emptyBudget = (p:string):Row => ({ano:Number(yearMonth(p).slice(0,4)),mes:Number(yearMonth(p).slice(5,7)),categoria_id:'',limite:0,observacao:'',ativo:true})
const emptyGoal = ():Row => ({nome:'',valor_meta:0,valor_atual:0,data_alvo:'',status:'ATIVA',observacao:''})
const goals = ['ATIVA','CONCLUIDA','PAUSADA','CANCELADA'] as const
const goalLabel:Record<string,string> = {ATIVA:'Ativa',CONCLUIDA:'Concluída',PAUSADA:'Pausada',CANCELADA:'Cancelada'}

export default function PlanningManager({mode}:{mode:Mode}) {
  const budget=mode==='orcamentos'
  const title=budget?'Orçamentos':'Metas'
  const {periodo,label}=usePeriod()
  const [base,setBase]=useState<BaseFinanceira|null>(null)
  const [rows,setRows]=useState<Row[]>([])
  const [categories,setCategories]=useState<Categoria[]>([])
  const [canManage,setCanManage]=useState(false)
  const [loading,setLoading]=useState(true)
  const [busy,setBusy]=useState(false)
  const [error,setError]=useState('')
  const [form,setForm]=useState<Row|null>(null)
  const [filter,setFilter]=useState('')
  const start=()=>{setError('');setForm(budget?emptyBudget(periodo):emptyGoal())}
  useRegisterQuickCreate(budget?'Novo orçamento':'Nova meta',start,canManage&&!loading)

  const reload=async (b:BaseFinanceira)=>{
    const [data,cats]=await Promise.all([listarTabelaGerencial(mode,b.id),budget?listarCategorias(b.id):Promise.resolve([])])
    setRows(data)
    setCategories(cats)
  }
  useEffect(()=>{
    let active=true
    setLoading(true)
    const load=async()=>{
      try {
        const b=await obterBaseAtiva()
        if(!b)throw new Error('Base financeira não encontrada.')
        const acesso=await obterAcessoBase(b.id)
        if(!acesso||!temPermissao(acesso,budget?'orcamentos.view':'metas.view'))throw new Error('Sem permissão para consultar este módulo.')
        const [data,cats]=await Promise.all([listarTabelaGerencial(mode,b.id),budget?listarCategorias(b.id):Promise.resolve([])])
        if(active){setBase(b);setCanManage(temPermissao(acesso,budget?'orcamentos.manage':'metas.manage'));setRows(data);setCategories(cats)}
      }catch(e){if(active)setError(e instanceof Error?e.message:'Erro ao carregar dados.')}
      finally{if(active)setLoading(false)}
    }
    void load()
    return()=>{active=false}
  },[mode,budget])

  const visible=useMemo(()=>rows.filter(r=>{
    const matchPeriod=!budget||periodo==='ALL'||(Number(r.ano)===Number(periodo.slice(0,4))&&Number(r.mes)===Number(periodo.slice(5,7)))
    const cat=categories.find(c=>c.id===r.categoria_id)?.nome||''
    return matchPeriod&&(!filter||`${r.nome||''} ${cat} ${r.observacao||''}`.toLowerCase().includes(filter.toLowerCase()))
  }),[rows,periodo,budget,categories,filter])
  const total=visible.reduce((n,r)=>n+Number(budget?r.limite:r.valor_meta||0),0)
  const current=visible.reduce((n,r)=>n+Number(r.valor_atual||0),0)
  const edit=(r:Row)=>{if(canManage){setError('');setForm({...r,data_alvo:r.data_alvo?String(r.data_alvo).slice(0,10):''})}}
  const save=async()=>{
    if(!base||!form||!canManage)return
    if(budget&&(!form.categoria_id||!(Number(form.limite)>=0)))return setError('Selecione uma categoria e informe um limite válido.')
    if(!budget&&(!String(form.nome||'').trim()||!(Number(form.valor_meta)>0)||!(Number(form.valor_atual)>=0)))return setError('Informe o nome e os valores válidos da meta.')
    setBusy(true);setError('')
    try{
      const now=new Date().toISOString()
      const payload=budget?{
        base_id:base.id,ano:Number(form.ano),mes:Number(form.mes),
        categoria_id:form.categoria_id,limite:Number(form.limite),observacao:String(form.observacao||'').trim()||null,
        ativo:form.ativo!==false,atualizado_em:now,
      }:{
        base_id:base.id,nome:String(form.nome).trim(),valor_meta:Number(form.valor_meta),
        valor_atual:Number(form.valor_atual),data_alvo:form.data_alvo||null,status:form.status||'ATIVA',
        observacao:String(form.observacao||'').trim()||null,atualizado_em:now,
      }
      const query=form.id
        ? dataClient.from(mode).update(payload).eq('id',form.id).eq('base_id',base.id)
        : dataClient.from(mode).insert({...payload,criado_em:now})
      const {error:dbError}=await query
      if(dbError)throw dbError
      await reload(base)
      setForm(null)
    }catch(e){setError(e instanceof Error?e.message:'Não foi possível salvar.')}
    finally{setBusy(false)}
  }
  return <section className="page planning-page">
    <div className="page-heading">
      <div><h1>{title}</h1><p>{budget?'Controle de limites por categoria e competência.':'Acompanhamento dos objetivos financeiros.'} <strong>{label}</strong></p></div>
      {canManage&&<button type="button" className="button primary compact" onClick={start}><Plus size={16}/> {budget?'Novo orçamento':'Nova meta'}</button>}
    </div>
    {error&&!form&&<div className="error-box page-error" role="alert">{error}</div>}
    {loading?<div className="state-card">Carregando {title.toLowerCase()}...</div>:<>
      <div className="planning-summary">
        <article><small>{budget?'Limites planejados':'Total das metas'}</small><strong className="privacy-value">{currency(total)}</strong></article>
        <article><small>{budget?'Categorias planejadas':'Valor acumulado'}</small><strong className="privacy-value">{budget?String(visible.length):currency(current)}</strong></article>
        <article><small>{budget?'Orçamentos ativos':'Progresso total'}</small><strong>{budget?String(visible.filter(r=>r.ativo!==false).length):`${total>0?Math.min(100,Math.round(current/total*100)):0}%`}</strong></article>
      </div>
      <FilterToolbar value={filter} onChange={setFilter} placeholder={budget?'Buscar categoria ou observação...':'Buscar meta...'}><span className="ff-filter-count">{visible.length} registro(s)</span></FilterToolbar>
      <div className="planning-list">
        {visible.map(r=>{
          const name=budget?categories.find(c=>c.id===r.categoria_id)?.nome||'Categoria não encontrada':r.nome
          const value=Number(budget?r.limite:r.valor_meta)
          const progress=budget?null:(value>0?Math.min(100,Math.round(Number(r.valor_atual||0)/value*100)):0)
          return <button type="button" key={r.id} className="planning-item" onClick={()=>edit(r)} disabled={!canManage}>
            <span className="planning-icon">{budget?<Wallet size={19}/>:<Target size={19}/>}</span>
            <span className="planning-item-main"><strong>{name}</strong><small>{budget?`${String(r.mes).padStart(2,'0')}/${r.ano} · ${r.ativo===false?'Inativo':'Ativo'}`:`${goalLabel[r.status]||r.status}${r.data_alvo?' · Até '+String(r.data_alvo).slice(0,10).split('-').reverse().join('/'):''}`}</small>{progress!==null&&<span className="planning-progress"><i style={{width:`${progress}%`}}/></span>}</span>
            <span className="planning-item-value"><strong className="privacy-value">{currency(value)}</strong>{progress!==null&&<small>{progress}% concluído</small>}{canManage&&<Edit3 size={14}/>}</span>
          </button>
        })}
        {!visible.length&&<div className="mobile-empty-state">Nenhum registro encontrado. {canManage?'Use o botão + para cadastrar.':''}</div>}
      </div>
    </>}
    <Modal aberto={!!form} titulo={form?.id?(budget?'Editar orçamento':'Editar meta'):(budget?'Novo orçamento':'Nova meta')} subtitulo={budget?'Defina o limite de despesas por categoria.':'Defina seu objetivo e acompanhe a evolução.'} onClose={()=>!busy&&setForm(null)}>
      {form&&<div className="form-grid two">
        {budget?<><label>Categoria<select value={form.categoria_id||''} onChange={e=>setForm({...form,categoria_id:e.target.value})}><option value="">Selecione</option>{categories.filter(c=>c.tipo==='DESPESA'&&(c.ativa!==false||c.id===form.categoria_id)).map(c=><option key={c.id} value={c.id}>{c.nome}</option>)}</select></label>
          <label>Ano<input type="number" min="2000" max="2200" value={form.ano} onChange={e=>setForm({...form,ano:Number(e.target.value)})}/></label>
          <label>Mês<select value={form.mes} onChange={e=>setForm({...form,mes:Number(e.target.value)})}>{Array.from({length:12},(_,i)=><option key={i} value={i+1}>{String(i+1).padStart(2,'0')}</option>)}</select></label>
          <label>Limite (R$)<MoneyInput value={Number(form.limite||0)} onChange={v=>setForm({...form,limite:v})}/></label>
          <label className="check-field"><input type="checkbox" checked={form.ativo!==false} onChange={e=>setForm({...form,ativo:e.target.checked})}/> Ativo</label>
        </>:<><label className="span-2">Nome da meta<input value={form.nome||''} onChange={e=>setForm({...form,nome:e.target.value})} placeholder="Ex.: Reserva de emergência"/></label>
          <label>Valor desejado (R$)<MoneyInput value={Number(form.valor_meta||0)} onChange={v=>setForm({...form,valor_meta:v})}/></label>
          <label>Valor acumulado (R$)<MoneyInput value={Number(form.valor_atual||0)} onChange={v=>setForm({...form,valor_atual:v})}/></label>
          <label>Data-alvo<input type="date" value={form.data_alvo||''} onChange={e=>setForm({...form,data_alvo:e.target.value})}/></label>
          <label>Situação<select value={form.status} onChange={e=>setForm({...form,status:e.target.value})}>{goals.map(s=><option key={s} value={s}>{goalLabel[s]}</option>)}</select></label>
        </>}
        <label className="span-2">Observações<textarea rows={3} value={form.observacao||''} onChange={e=>setForm({...form,observacao:e.target.value})}/></label>
      </div>}
      {error&&<div className="error-box" role="alert">{error}</div>}
      <div className="modal-actions"><button className="button ghost" disabled={busy} onClick={()=>setForm(null)}>Cancelar</button><button className="button primary" disabled={busy} onClick={()=>void save()}>{busy?'Salvando...':'Salvar'}</button></div>
    </Modal>
  </section>
}

